import { Injectable, NgZone, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { CoreTargetService } from 'src/app/core/services/target.service';
import { LOCAL_STORAGE_CONFIG } from 'src/app/core/services/storage.tokens';
import { TimezoneService } from 'src/app/services/timezone.service';
import { SUPPORT_DIAGNOSTICS_LIMITS } from '../support-system.config';
import {
  SupportDiagnosticsSnapshot,
  SupportNavigationEntry,
  SupportNavigationKind,
  SupportRequestLogEntry,
} from '../support-system.models';
import { redactUrl, serializeBody } from './support-diagnostics.utils';

interface PersistedActivity {
  requests: SupportRequestLogEntry[];
  navigation: SupportNavigationEntry[];
  nextId: number;
}

/**
 * Keeps a short rolling history of API calls and navigation (routes, in-page
 * tab switches, browser-tab visibility) so an issue report can carry what the
 * user did right before it. Started once at bootstrap; HTTP entries are fed
 * by `SupportActivityInterceptor`.
 *
 * The buffer lives in sessionStorage so a reload — the usual reaction to a
 * broken page — doesn't wipe it. Logout clears sessionStorage.
 */
@Injectable({ providedIn: 'root' })
export class SupportActivityTrackerService {
  private router = inject(Router);
  private zone = inject(NgZone);
  private translate = inject(TranslateService);
  private targetService = inject(CoreTargetService);
  private timezoneService = inject(TimezoneService);
  private storageConfig = inject(LOCAL_STORAGE_CONFIG, { optional: true });

  private readonly limits = SUPPORT_DIAGNOSTICS_LIMITS;

  private requests: SupportRequestLogEntry[] = [];
  private navigation: SupportNavigationEntry[] = [];
  private nextId = 1;
  private started = false;
  private persistTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly startTimes = new Map<number, number>();

  start(): void {
    if (this.started) return;
    this.started = true;

    this.restore();
    this.pushNavigation('load', this.currentUrl());

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.pushNavigation('route', e.urlAfterRedirects));

    this.zone.runOutsideAngular(() => {
      document.addEventListener('click', this.onTabClick, true);
      document.addEventListener('keydown', this.onTabKeydown, true);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    });
  }

  /* ------------------------------ Requests ------------------------------ */

  /** Returns the entry id, or null when the request isn't worth tracking. */
  beginRequest(method: string, url: string, body: unknown): number | null {
    if (!this.shouldTrack(url)) return null;

    const id = this.nextId++;
    this.startTimes.set(id, performance.now());
    this.pushRequest({
      id,
      method,
      url: redactUrl(url),
      startedAt: new Date().toISOString(),
      durationMs: null,
      status: null,
      pending: true,
      requestBody: serializeBody(body, this.limits.maxBodyChars),
    });
    return id;
  }

  completeRequest(
    id: number,
    status: number,
    statusText?: string,
    errorBody?: unknown,
  ): void {
    this.updateRequest(id, (entry) => {
      entry.status = status;
      entry.statusText = statusText || undefined;
      if (status === 0 || status >= 400) {
        entry.errorBody = serializeBody(errorBody, this.limits.maxBodyChars);
      }
    });
  }

  cancelRequest(id: number): void {
    this.updateRequest(id, (entry) => (entry.cancelled = true));
  }

  /* ------------------------------ Snapshot ------------------------------ */

  snapshot(): SupportDiagnosticsSnapshot {
    const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));
    return {
      capturedAt: new Date().toISOString(),
      context: {
        url: this.currentUrl(),
        target: this.targetService.getSelectedTarget('target') || '',
        language: this.translate.currentLang || 'en',
        timezone: this.timezoneService.getTimezone(),
        userAgent: navigator.userAgent,
        viewport: { width: window.innerWidth, height: window.innerHeight },
        darkMode: document.body.classList.contains('dark-theme'),
        appVersion: this.storageConfig?.version || '',
      },
      requests: clone(this.requests),
      navigation: clone(this.navigation),
    };
  }

  /** Drops the history, e.g. when a different user signs in. */
  reset(): void {
    this.requests = [];
    this.navigation = [];
    this.startTimes.clear();
    this.schedulePersist();
  }

  /* ------------------------------ Internals ----------------------------- */

  private shouldTrack(url: string): boolean {
    return !(
      url.startsWith('data:') ||
      url.includes('/assets/') ||
      url.startsWith('assets/')
    );
  }

  private pushRequest(entry: SupportRequestLogEntry): void {
    this.requests.push(entry);
    const overflow = this.requests.length - this.limits.maxRequests;
    if (overflow > 0) {
      this.requests
        .splice(0, overflow)
        .forEach((dropped) => this.startTimes.delete(dropped.id));
    }
    this.schedulePersist();
  }

  private updateRequest(
    id: number,
    apply: (entry: SupportRequestLogEntry) => void,
  ): void {
    const entry = this.requests.find((r) => r.id === id);
    const startedAt = this.startTimes.get(id);
    this.startTimes.delete(id);
    if (!entry) return;

    entry.pending = false;
    if (startedAt !== undefined) {
      entry.durationMs = Math.round(performance.now() - startedAt);
    }
    apply(entry);
    this.schedulePersist();
  }

  private pushNavigation(
    kind: SupportNavigationKind,
    url: string,
    label?: string,
  ): void {
    const safeUrl = redactUrl(url);
    const last = this.navigation[this.navigation.length - 1];

    // The first NavigationEnd repeats the 'load' entry; same-tab re-clicks
    // and repeated visibility events add nothing either.
    if (last && last.url === safeUrl) {
      if (kind === 'route' && (last.kind === 'route' || last.kind === 'load'))
        return;
      if (kind === last.kind && label === last.label) return;
    }

    this.navigation.push({
      kind,
      at: new Date().toISOString(),
      url: safeUrl,
      label,
    });
    const overflow = this.navigation.length - this.limits.maxNavigation;
    if (overflow > 0) this.navigation.splice(0, overflow);
    this.schedulePersist();
  }

  private onTabClick = (event: Event): void => {
    this.recordTab(event.target);
  };

  private onTabKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      this.recordTab(event.target);
    }
  };

  /** Any ARIA tab (all mat-tab-groups) or an element marked `data-support-tab`. */
  private recordTab(target: EventTarget | null): void {
    if (!(target instanceof Element)) return;
    const tab = target.closest<HTMLElement>('[role="tab"], [data-support-tab]');
    if (!tab || tab.closest('app-support-chat-box')) return;
    if (
      tab.getAttribute('aria-selected') === 'true' ||
      tab.getAttribute('aria-disabled') === 'true'
    ) {
      return;
    }

    const label = (
      tab.getAttribute('data-support-tab') ||
      tab.getAttribute('aria-label') ||
      tab.textContent ||
      ''
    )
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 80);
    if (label) this.pushNavigation('tab', this.currentUrl(), label);
  }

  private onVisibilityChange = (): void => {
    this.pushNavigation(
      'visibility',
      this.currentUrl(),
      document.visibilityState === 'hidden' ? 'hidden' : 'visible',
    );
  };

  private currentUrl(): string {
    return this.router.url && this.router.url !== '/'
      ? this.router.url
      : location.pathname + location.search;
  }

  private schedulePersist(): void {
    if (this.persistTimer) return;
    this.zone.runOutsideAngular(() => {
      this.persistTimer = setTimeout(() => {
        this.persistTimer = null;
        this.persist();
      }, 1000);
    });
  }

  private persist(): void {
    const data: PersistedActivity = {
      requests: this.requests,
      navigation: this.navigation,
      nextId: this.nextId,
    };
    try {
      sessionStorage.setItem(
        this.limits.sessionStorageKey,
        JSON.stringify(data),
      );
    } catch {}
  }

  private restore(): void {
    try {
      const raw = sessionStorage.getItem(this.limits.sessionStorageKey);
      if (!raw) return;
      const data = JSON.parse(raw) as PersistedActivity;

      this.requests = (data.requests || []).map((r) =>
        // Requests in flight when the page unloaded never finished.
        r.pending ? { ...r, pending: false, cancelled: true } : r,
      );
      this.navigation = data.navigation || [];
      this.nextId = data.nextId || 1;
    } catch {}
  }
}
